import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Channel } from "@/types/channel";
import { Book, MessageSquare, Radio } from "lucide-react";

interface ChannelCardsProps {
    channels: Channel[];
    onEdit: (channel: Channel) => void;
    onToggleStatus: (channelId: string, isActive: boolean) => void;
    onGenerateQRCode: (channelId: string) => void;
}

const getChannelTypeLabel = (type: string) => {
    switch (type) {
        case "slack":
            return "Slack";
        default:
            return type;
    }
};

export default function ChannelCards({ channels, onEdit, onToggleStatus }: ChannelCardsProps) {
    if (!channels.length) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border/70 bg-card/50 py-16 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted">
                    <Radio className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="font-medium">Nenhum canal configurado ainda</p>
                <p className="max-w-xs text-sm text-muted-foreground">
                    Configure uma integração acima para começar a receber atendimentos por aqui.
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {channels.map((channel) => (
                <div
                    key={channel._id}
                    className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-500">
                                <MessageSquare className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <p className="truncate font-semibold leading-tight">{channel.name}</p>
                                <p className="text-xs text-muted-foreground">{getChannelTypeLabel(channel.type)}</p>
                            </div>
                        </div>
                        <Badge variant={channel.isActive ? "default" : "secondary"} className="shrink-0">
                            {channel.isActive ? "Ativo" : "Inativo"}
                        </Badge>
                    </div>

                    {channel.knowledgeBaseIds && channel.knowledgeBaseIds.length > 0 && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Book className="h-3.5 w-3.5" />
                            {channel.knowledgeBaseIds.length} base{channel.knowledgeBaseIds.length !== 1 ? "s" : ""} de conhecimento vinculada{channel.knowledgeBaseIds.length !== 1 ? "s" : ""}
                        </div>
                    )}

                    <div className="mt-auto flex items-center justify-between border-t border-border/60 pt-4">
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={channel.isActive}
                                onCheckedChange={(checked) => onToggleStatus(channel._id, checked)}
                            />
                            <span className="text-sm text-muted-foreground">
                                {channel.isActive ? "Ativo" : "Inativo"}
                            </span>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => onEdit(channel)}>
                            Editar
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    );
}